// frontend/src/components/Profile/ProfileHeader.tsx
import React, { useState } from 'react';
import { 
  Camera, 
  CheckCircle, 
  Loader2,
  UserCheck,
  Mail,
  Phone
} from 'lucide-react';
import { UserProfile } from './types';

interface ProfileHeaderProps {
  user: UserProfile;
  onUpdateAvatar?: (file: File) => Promise<{success: boolean; message?: string; url?: string}>;
  isUploading?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ 
  user, 
  onUpdateAvatar, 
  isUploading 
}) => {
  const [dragActive, setDragActive] = useState(false);
  
  const getUserInitials = () => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) return firstName.charAt(0).toUpperCase();
    return user.username?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) return user.first_name;
    return user.username || user.email;
  };

  const getRoleDisplayName = () => {
    switch (user.role) {
      case 'admin':
      case 'super_admin':
        return 'Administrator';
      case 'provider':
        return 'Service Provider';
      case 'customer':
      case 'user':
        return 'Customer';
      default:
        return user.role;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUpdateAvatar) {
      await onUpdateAvatar(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && onUpdateAvatar) {
      await onUpdateAvatar(file);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-start space-x-6">
        {/* Avatar */}
        <div className="relative">
          <div
            className={`w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold transition-colors ${
              dragActive 
                ? 'border-2 border-dashed border-blue-500 bg-blue-50' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            ) : user.profile_image ? (
              <img 
                src={user.profile_image} 
                alt="Profile" 
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-gray-600">{getUserInitials()}</span>
            )}
          </div>
          
          {onUpdateAvatar && (
            <>
              <button
                className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transition-colors"
                onClick={() => document.getElementById('avatar-upload')?.click()}
                disabled={isUploading}
              >
                <Camera className="w-3 h-3" />
              </button>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {getUserDisplayName()}
          </h1>
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center">
              <UserCheck className="w-4 h-4 mr-2" />
              <span>{getRoleDisplayName()}</span>
            </div>
            <div className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              <span>{user.email}</span>
              {user.email_verified_at && (
                <CheckCircle className="w-4 h-4 ml-2 text-green-500" />
              )}
            </div>
            {user.phone_number && (
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                <span>{user.phone_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex flex-col items-end space-y-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            user.is_active 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
          <span className="text-xs text-gray-500">
            Member since {new Date(user.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;